const OAUTH_URL_KEYS = ['url', 'authorization_url', 'auth_url', 'redirect_url'] as const;

export function readOAuthRedirectUrl(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const row = data as Record<string, unknown>;
  for (const key of OAUTH_URL_KEYS) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

export const EMAIL_OAUTH_SESSION_KEY = 'fd_email_oauth_provider';
export const EMAIL_OAUTH_FLOW_KEY = 'fd_email_oauth_flow';
export const EMAIL_OAUTH_CALLBACK_PARAMS_KEY = 'fd_email_oauth_callback_params';

export type EmailOauthProvider = 'gmail' | 'outlook';
export type EmailOauthFlow = 'accounts' | 'settings';

export function rememberEmailOauthSession(provider: EmailOauthProvider, flow: EmailOauthFlow): void {
  sessionStorage.setItem(EMAIL_OAUTH_SESSION_KEY, provider);
  sessionStorage.setItem(EMAIL_OAUTH_FLOW_KEY, flow);
}

export function readEmailOauthSession(): {
  provider: EmailOauthProvider;
  flow: EmailOauthFlow;
} {
  const providerRaw = sessionStorage.getItem(EMAIL_OAUTH_SESSION_KEY);
  const flowRaw = sessionStorage.getItem(EMAIL_OAUTH_FLOW_KEY);
  return {
    provider: providerRaw === 'outlook' ? 'outlook' : 'gmail',
    flow: flowRaw === 'settings' ? 'settings' : 'accounts',
  };
}

export function clearEmailOauthSession(): void {
  sessionStorage.removeItem(EMAIL_OAUTH_SESSION_KEY);
  sessionStorage.removeItem(EMAIL_OAUTH_FLOW_KEY);
}

export function readOAuthCallbackParams(): { code: string; state: string; error: string } {
  const params = new URLSearchParams(window.location.search);
  return {
    code: params.get('code') ?? '',
    state: params.get('state') ?? '',
    error: params.get('error') ?? '',
  };
}

export function readCapturedOAuthCallbackParams(): { code: string; state: string } | null {
  const raw = sessionStorage.getItem(EMAIL_OAUTH_CALLBACK_PARAMS_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as { code?: string; state?: string };
    if (
      typeof parsed.code === 'string' &&
      parsed.code.trim() &&
      typeof parsed.state === 'string' &&
      parsed.state.trim()
    ) {
      return { code: parsed.code, state: parsed.state };
    }
  } catch {
    return null;
  }

  return null;
}

/** Persist callback params before scrubbing the URL (Strict Mode safe). */
export function captureOAuthCallbackParams(): { code: string; state: string; error: string } {
  const fromUrl = readOAuthCallbackParams();

  if (fromUrl.error) {
    return fromUrl;
  }

  if (fromUrl.code && fromUrl.state) {
    sessionStorage.setItem(
      EMAIL_OAUTH_CALLBACK_PARAMS_KEY,
      JSON.stringify({ code: fromUrl.code, state: fromUrl.state }),
    );
    scrubOAuthCallbackUrl();
    return fromUrl;
  }

  const stored = readCapturedOAuthCallbackParams();
  if (stored) {
    return { code: stored.code, state: stored.state, error: '' };
  }

  return fromUrl;
}

export function clearOAuthCallbackCapture(): void {
  sessionStorage.removeItem(EMAIL_OAUTH_CALLBACK_PARAMS_KEY);
}

export function scrubOAuthCallbackUrl(): void {
  window.history.replaceState({}, document.title, window.location.pathname);
}
